<template>
	<div class="view-payment-breakdown">
		<Modal
			:show="showMakePayment"
			content_class="col-12 col-md-9 col-lg-7 col-xl-6 confirm-modal"
		>
			<template slot="content" v-if="selectInvoiceDetails">
				<div v-for="invoice in selectInvoiceDetails">
					<div class="modal-header">
						<div class="content-title">Pay Invoice</div>
						<div
							class="close-modal"
							@click="$store.commit('showMakePayment', false)"
						>
							<i class="fa fa-times"></i>
						</div>
					</div>

					<div class="modal-content">
						<div class="row">
							<div class="col-12">
								<div class="payment-card">
									<!-- Invoice Summary -->
									<div class="invoice-summary">
										<p class="summary-text">
											You are paying for Invoice Ref:
											<span
												class="summary-value tx-uppercase"
												>{{
													invoice.invoiceSystemRef
												}}</span
											>
											-
											<span class="summary-value"
												>{{ invoice.invoiceCurrency }}
												{{
													utils.numberWithCommas2(
														invoice.invoiceAmount,
													)
												}}</span
											>
										</p>
										<!-- Commented out Available Balance section -->
										<!--
                               <div class="summary-row">
                                  <span class="summary-label">Available Balance:</span>
                                  <span class="summary-value" :class="{'tx-red': invoice_details.invoice_amount > acc_balance}">
                                     {{ acc_balance_currency }} {{ utils.numberWithCommas2(Math.round(acc_balance * 100) / 100) }}
                                  </span>
                               </div>
                               -->
									</div>

									<!-- Payment Method Selector -->
									<div
										keyboard-navigable
										class="payment-method-selector"
									>
										<div class="method-tabs">
											<button
												v-for="method in paymentMethods"
												:key="method.id"
												:class="[
													'method-tab',
													{
														active:
															selectedMethod ===
															method.id,
													},
												]"
												@click="
													selectedMethod = method.id
												"
											>
												{{ method.name }}
											</button>
										</div>
									</div>

									<!-- Dynamic Input Area -->
									<div class="input-area">
										<!-- Mpesa Input -->
										<div
											v-if="selectedMethod === 'mpesa'"
											class="input-group"
										>
											<label class="input-label"
												>Phone Number</label
											>
											<input
												v-model="mpesaPhone"
												type="tel"
												placeholder="Enter phone number"
												class="input-field"
												:class="{
													'input-error': mpesaError,
												}"
												@input="validateMpesa"
											/>
											<span
												v-if="mpesaError"
												class="error-message"
												>{{ mpesaError }}</span
											>
										</div>

										<!-- Card Input -->
										<div
											v-if="selectedMethod === 'card'"
											class="input-group"
										>
											<label class="input-label"
												>Card Number</label
											>
											<input
												v-model="cardNumber"
												type="text"
												placeholder="Enter card number"
												class="input-field"
												:class="{
													'input-error':
														cardNumberError,
												}"
												@input="validateCardNumber"
											/>
											<span
												v-if="cardNumberError"
												class="error-message"
												>{{ cardNumberError }}</span
											>

											<div class="card-details">
												<div
													class="input-group half-width"
												>
													<label class="input-label"
														>Expiry (MM/YY)</label
													>
													<input
														v-model="cardExpiry"
														type="text"
														placeholder="MM/YY"
														class="input-field"
														:class="{
															'input-error':
																cardExpiryError,
														}"
														@input="
															validateCardExpiry
														"
													/>
													<span
														v-if="cardExpiryError"
														class="error-message"
														>{{
															cardExpiryError
														}}</span
													>
												</div>
												<div
													class="input-group half-width"
												>
													<label class="input-label"
														>CVV</label
													>
													<input
														v-model="cardCvv"
														type="text"
														placeholder="CVV"
														class="input-field"
														:class="{
															'input-error':
																cardCvvError,
														}"
														@input="validateCardCvv"
													/>
													<span
														v-if="cardCvvError"
														class="error-message"
														>{{
															cardCvvError
														}}</span
													>
												</div>
											</div>
										</div>

										<!-- Bank Input -->
										<div
											v-if="selectedMethod === 'bank'"
											class="input-group"
										>
											<label class="input-label"
												>Bank Account</label
											>
											<select
												v-model="bankAccount"
												class="input-field"
											>
												<option value="" disabled>
													Select bank account
												</option>
												<option
													v-for="account in bankAccounts"
													:key="account.id"
													:value="account.id"
												>
													{{ account.name }}
												</option>
											</select>
											<div class="bank-details">
												<p>
													Reference:
													<strong>{{
														invoice.invoiceSystemRef
													}}</strong>
												</p>
												<p>
													Please use the invoice
													reference when making the
													bank transfer.
												</p>
											</div>
										</div>
									</div>

									<!-- Terms and Conditions -->
									<div class="terms-section">
										<p class="terms-text">
											By proceeding, you are agreeing to
											the
											<a
												href="/terms"
												target="_blank"
												rel="noopener noreferrer"
												class="terms-link"
											>
												Terms and Conditions </a
											>.
										</p>
									</div>

									<!-- Action Buttons -->
									<div class="action-section">
										<button
											v-if="isValidInput"
											class="action-button pay-btn"
											@click="initiatePayment()"
										>
											{{
												paymentStatus === 'processing'
													? 'Processing...'
													: 'Make Payment'
											}}
										</button>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</template>
		</Modal>
	</div>
</template>

<script>
import {
	CheckPaymentDetails,
	RequestPayment,
} from '../../insurance/api/insurance';
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex';
export default {
	components: {
		Modal: () => import('../templates/others/modal'),
		Form: () => import('@/libs/clarks/components/form'),
		Dropdown: () => import('@/libs/clarks/components/dropdown/dropdown'),
		DropdownItem: () =>
			import('@/libs/clarks/components/dropdown/dropdown-item'),
		DropdownMenu: () =>
			import('@/libs/clarks/components/dropdown/dropdown-menu'),
	},
	computed: {
		...mapGetters([
			'loading',
			'FeeInvoiceModel',
			'payment_breakdown',
			'get_finance_data',
			'invoice_breakdown',
			'selected_invoice',
			'acc_balance_currency',
			'invoice_details',
			'showMakePayment',
			'acc_balance',
		]),
		...mapGetters('invoices', ['selectInvoiceDetails']),
		...mapState('insurance/motorPrivate', ['paymentStatus']),

		isValidInput() {
			if (this.selectedMethod === 'mpesa') return !this.mpesaError;
			if (this.selectedMethod === 'card')
				return (
					!this.cardNumberError &&
					!this.cardExpiryError &&
					!this.cardCvvError
				);
			if (this.selectedMethod === 'bank') return !!this.bankAccount;
			return false;
		},
	},
	data() {
		return {
			utils,
			moment,
			selectedMethod: 'mpesa',
			paymentMethods: [
				{ id: 'mpesa', name: 'Mpesa' },
				{ id: 'card', name: 'Card' },
				{ id: 'bank', name: 'Bank' },
			],
			bankAccounts: [
				{ id: 'bank1', name: 'Bank A - 1234567890' },
				{ id: 'bank2', name: 'Bank B - 0987654321' },
			],
			mpesaPhone: '',
			mpesaError: '',
			cardNumber: '',
			cardNumberError: '',
			cardExpiry: '',
			cardExpiryError: '',
			cardCvv: '',
			cardCvvError: '',
			bankAccount: '',
		};
	},
	methods: {
		...mapActions(['paySchoolInvoice', 'payInvoice']),
		...mapMutations('insurance/motorPrivate', ['SET_PAYMENT_STATUS']),
		...mapActions('quotation', ['setPaymentResult']),
		...mapActions('insurance/motorPrivate', ['setPaymentStatus']),
		validateMpesa() {
			const phoneRegex = /^(0|1)\d{9}$/;
			this.mpesaError = phoneRegex.test(this.mpesaPhone)
				? ''
				: 'Please enter a valid phone number (07xxxxxxx or 01xxxxxxx)';
		},
		validateCardNumber() {
			const cardRegex = /^\d{16}$/;
			this.cardNumberError = cardRegex.test(
				this.cardNumber.replace(/\s/g, ''),
			)
				? ''
				: 'Please enter a valid 16-digit card number';
		},
		validateCardExpiry() {
			const expiryRegex = /^(01-9]|1[0-2])\/\d{2}$/;
			this.cardExpiryError = expiryRegex.test(this.cardExpiry)
				? ''
				: 'Please enter a valid expiry date (MM/YY)';
		},
		validateCardCvv() {
			const cvvRegex = /^\d{3,4}$/;
			this.cardCvvError = cvvRegex.test(this.cardCvv)
				? ''
				: 'Please enter a valid CVV';
		},
		paymentData() {
			const phone = this.mpesaPhone.trim();
			const cleaned = phone.startsWith('0') ? phone.slice(1) : phone;
			return {
				invoiceSystemRef: this.selectInvoiceDetails[0].invoiceSystemRef,
				invoicePaymentDetails: {
					paymentProvider: 'mpesa',
					params: {
						PhoneNumber: '254' + cleaned,
					},
				},
			};
		},
		async pollPaymentStatus(paymentRef, retries = 6, interval = 5000) {
			for (let i = 0; i < retries; i++) {
				await new Promise((resolve) => setTimeout(resolve, interval));

				const response = await CheckPaymentDetails({
					paymentSystemRef: paymentRef,
				});
				console.log(`Poll #${i + 1}:`, response.data);

				if (
					response.data.paymentRequestStatus === 'success' ||
					response.data.paymentRequestStatus === 'failed'
				) {
					return response;
				}
			}

			return {
				status: false,
				data: {
					paymentRequestStatus: 'failed',
					statusMessage: 'Payment timeout',
				},
			};
		},

		async initiatePayment() {
			const payload = this.paymentData();
			//   console.log("Initiating payment with data:", payload);

			this.setPaymentStatus('processing');

			try {
				const res = await RequestPayment(payload);
				console.log('Payment response:', res);

				if (!res || !res.status || !res.data.paymentRef) {
					throw new Error('Invalid API response');
				}

				const paymentRef = res.data.paymentRef;

				const response = await this.pollPaymentStatus(paymentRef);

				if (response && response.status && response.data) {
					const { paymentRequestStatus, statusMessage } =
						response.data;

					if (paymentRequestStatus === 'success') {
						this.setPaymentStatus('success');
						this.setPaymentResult(response.data);
						this.$store.commit('showMakePayment', false);
						Event.$emit('notifications:show', {
							type: 'success',
							title: 'Payment Successful',
							message:
								'Your payment has been successfully processed.',
							duration: 3000,
						});
					} else {
						this.setPaymentStatus('pending');
						Event.$emit('notifications:show', {
							type: 'error',
							title: 'Payment Failed',
							message: statusMessage || 'No payment received',
							duration: 6000,
						});
					}
				}
			} catch (error) {
				console.error('Payment error:', error);

				this.setPaymentStatus('failed');

				Event.$emit('notifications:show', {
					type: 'error',
					title: 'Payment Error',
					message:
						error.response.data.errors ||
						error.message ||
						'An unexpected error occurred',
					duration: 5000,
				});

				setTimeout(() => {
					this.setPaymentStatus('processing');
				}, 3000);
			}
		},
	},
	mounted() {},
};
</script>

<style lang="less">
@brand-magenta: #e91e63;
@brand-orange: #ff9800;
@brand-teal: #1db584;
@brand-blue: #2196f3;
@white: #ffffff;
@grey-50: #fafafa;
@grey-100: #f5f5f5;
@grey-200: #eeeeee;
@grey-400: #bdbdbd;
@grey-600: #757575;
@text-primary: #212121;
@text-secondary: #757575;
@text-muted: #9e9e9e;
@error-red: #f44336;

.view-payment-breakdown {
	.modal-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 16px 24px;
		background: @grey-50;
		border-bottom: 1px solid @grey-200;

		.content-title {
			font-size: 18px;
			font-weight: 700;
			color: @text-primary;
		}

		.close-modal {
			cursor: pointer;
			font-size: 16px;
			color: @text-secondary;
			transition: all 0.3s ease;

			&:hover {
				color: @brand-magenta;
			}
		}
	}

	.modal-content {
		padding: 24px;
	}

	.payment-card {
		border-radius: 12px;
		border: 1px solid @grey-200;
		background: @white;
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
		padding: 24px;
		transition: all 0.3s ease;

		&:hover {
			transform: translateY(-2px);
			box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
			border-color: @brand-teal;
		}

		.invoice-summary {
			margin-bottom: 24px;
			padding-bottom: 16px;
			border-bottom: 1px solid @grey-200;

			.summary-text {
				font-size: 14px;
				font-weight: 600;
				color: @text-secondary;
				margin: 0;

				.summary-value {
					font-weight: 700;
					color: @text-primary;

					&.tx-uppercase {
						text-transform: uppercase;
					}
				}
			}
		}

		.payment-method-selector {
			margin-bottom: 24px;

			.method-tabs {
				display: flex;
				gap: 8px;
				border-bottom: 1px solid @grey-200;
				padding-bottom: 8px;

				.method-tab {
					flex: 1;
					padding: 10px 16px;
					background: @grey-100;
					border: 1px solid @grey-200;
					border-radius: 8px;
					font-size: 14px;
					font-weight: 600;
					color: @text-secondary;
					text-align: center;
					cursor: pointer;
					transition: all 0.3s ease;

					&.active {
						background: @brand-blue;
						color: @white;
						border-color: @brand-blue;
					}

					&:hover {
						background: lighten(@brand-blue, 40%);
						color: @brand-blue;
					}
				}
			}
		}

		.input-area {
			margin-bottom: 24px;

			.input-group {
				margin-bottom: 16px;

				&.half-width {
					width: 48%;
					display: inline-block;

					&:first-child {
						margin-right: 4%;
					}
				}

				.input-label {
					display: block;
					font-size: 14px;
					font-weight: 600;
					color: @text-secondary;
					margin-bottom: 8px;
				}

				.input-field {
					width: 100%;
					padding: 10px;
					border: 1px solid @grey-200;
					border-radius: 8px;
					font-size: 14px;
					color: @text-primary;
					transition: all 0.3s ease;

					&:focus {
						outline: none;
						border-color: @brand-teal;
						box-shadow: 0 0 0 2px rgba(29, 181, 132, 0.2);
					}

					&.input-error {
						border-color: @error-red;
					}
				}

				.card-details {
					display: flex;
					gap: 4%;
					margin-top: 12px;
				}

				.bank-details {
					margin-top: 12px;
					font-size: 14px;
					color: @text-secondary;

					p {
						margin: 8px 0;
					}

					strong {
						color: @text-primary;
					}
				}

				.error-message {
					font-size: 12px;
					color: @error-red;
					margin-top: 4px;
					display: block;
				}
			}
		}

		.terms-section {
			margin-bottom: 24px;
			padding: 16px;
			background: @grey-50;
			border-radius: 8px;

			.terms-text {
				font-size: 14px;
				color: @text-secondary;
				margin-bottom: 12px;

				.terms-link {
					color: @brand-blue;
					text-decoration: none;
					font-weight: 600;

					&:hover {
						text-decoration: underline;
					}
				}
			}
		}

		.action-section {
			display: flex;
			justify-content: center;
			gap: 16px;

			.action-button {
				display: flex;
				align-items: center;
				justify-content: center;
				gap: 8px;
				padding: 12px 20px;
				border-radius: 8px;
				cursor: pointer;
				transition: all 0.3s ease;
				font-weight: 600;
				font-size: 14px;
				width: 100%;
				max-width: 200px;
				box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

				&:hover {
					transform: translateY(-2px);
					box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
				}

				&.pay-btn {
					background: @brand-teal;
					color: @white;

					&:hover {
						background: darken(@brand-teal, 5%);
					}
				}
			}
		}
	}

	@media (max-width: 768px) {
		.modal-content {
			padding: 16px;
		}

		.payment-card {
			padding: 16px;

			.method-tabs {
				flex-direction: column;
				gap: 8px;

				.method-tab {
					width: 100%;
				}
			}

			.input-area {
				.card-details {
					flex-direction: column;
					gap: 12px;

					.input-group.half-width {
						width: 100%;
						margin-right: 0;
					}
				}
			}

			.action-section {
				flex-direction: column;

				.action-button {
					max-width: 100%;
				}
			}
		}
	}

	.payment-card:focus {
		outline: 2px solid @brand-teal;
		outline-offset: 2px;
	}

	.payment-card.error {
		border-color: @error-red;
		background: #fff5f5;
	}
}
</style>
